import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { CropFieldsResponse, Employee, CropFieldLinkTypesEnum } from 'src/app/shared/models';
import { Environment } from 'src/environment';

import { TreatmentTypes } from '../../overview/models';
import {
  BemProduct,
  CreateOrderRequest,
  CreateOrderResponse,
  CropAidResponse,
  DeliveryPeriodMoment,
  DigitalProductsToSave,
  GetCropAidsResponse,
  GetCustomerPreferencesResponse,
  GetCustomerProductInventoryResponse,
  GetOrderLinesResponse,
  GetOrderPlanResponse,
  GetPackagesResponse,
  GetProductSubscriptionResponse,
  ImportStockFromTelersdashboardResponse,
  ModuleActivationResponse,
  GetSalesOrderPeriodsResponse,
  OrderLine,
  Package,
  ProductInventoryData,
  SalesOrderPeriod,
  SetDeliveryPeriodMomentsRequest,
  UpsertCustomerProductInventoryRequest,
  UpsertNumberOfPeriodsRequest,
  UpsertOrderLineRequest,
  UpsertPreferedDeliveryPeriodsRequest,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  protected http = inject(HttpClient);
  private readonly env = inject<Environment>(EnvironmentInjector);

  protected baseUrl = this.env.API_BASE_URL + '/service/orderplanning/secure';
  protected presalesOrderUrl = this.env.PRE_SALES_REBUILD_DONE ?
  this.env.APIM_BASE_URL + '/arable-service' :
  this.env.API_BASE_URL + '/service/orderplanning/secure';
  protected arableServiceUrl = this.env.APIM_BASE_URL + '/arable-service';
  protected arableDiseaseIntegrationUrl = this.env.APIM_BASE_URL + '/arable-disease-integration-service';
  protected fieldAdviceUrl = this.env.FIELD_ADVICE_REBUILD_DONE ? 
  this.env.APIM_BASE_URL + '/arable-service/advice/' :
  this.env.API_BASE_URL + '/service/field-advice/secure/';

  optimizeOrderplan(year: number, optimizeFullBoxes: boolean) {
    return this.http.post(`${this.baseUrl}/orderplan/optimize/${year}`, { optimizeFullBoxes });
  }

  getOrderLines(year: number, treatmentType: TreatmentTypes): Observable<OrderLine[]> {
    return this.http
      .get<GetOrderLinesResponse>(`${this.baseUrl}/orderline/${year}`, { params: { treatmentType } })
      .pipe(map((orderLinesResponse) => orderLinesResponse.orderLines));
  }

  deleteOrderLine(id: number) {
    return this.http.delete(`${this.baseUrl}/orderline/${id}`);
  }

  deleteOrderLines(ids: number[], year: number) {
    return this.http.post(`${this.baseUrl}/orderline/delete-orderlines`, { ids, year });
  }

  upsertOrderLine(year: number, orderLine: OrderLine, cropAidId: number) {
    const request: UpsertOrderLineRequest = {
      ...orderLine,
      year,
      cropAidId,
    };

    return this.http.post(`${this.baseUrl}/orderline`, request);
  }

  setDeliveryPeriodMoments(year: number, deliveryPeriodMoments: DeliveryPeriodMoment[]) {
    const request: SetDeliveryPeriodMomentsRequest = {
      year,
      deliveryPeriodMoments,
    };

    return this.http.post(`${this.baseUrl}/orderline/deliveryperiodmoments`, request);
  }

  getCropAids(): Observable<CropAidResponse[]> {
    return this.http.get<GetCropAidsResponse>(`${this.arableServiceUrl}/crop-aid?productType=1`).pipe(map((cropAidResponse) => cropAidResponse.cropAids));
  }

  getProprietaryCropAids(cropYear: number): Observable<CropAidResponse[]> {
    return this.http
      .get<GetCropAidsResponse>(`${this.arableServiceUrl}/crop-aid/proprietary/${cropYear}`)
      .pipe(map((cropAidResponse) => cropAidResponse.cropAids));
  }

  getPackages(): Observable<Package[]> {
    return this.http.get<GetPackagesResponse>(`${this.baseUrl}/package`).pipe(map((packageResponse) => packageResponse.packages));
  }

  getSalesOrderPeriods(year: number): Observable<SalesOrderPeriod[]> {
    const endpoint = this.env.PRE_SALES_REBUILD_DONE
      ? `salesorderperiod?year=${year}`
      : `salesorderperiod/year/${year}`;
    return this.http
      .get<GetSalesOrderPeriodsResponse>(`${this.presalesOrderUrl}/${endpoint}`)
      .pipe(map((salesOrderPeriodsResponse) => salesOrderPeriodsResponse.salesOrderPeriods));
  }

  upsertCustomerProductInventory(year: number, productInventories: ProductInventoryData[]) {
    const request: UpsertCustomerProductInventoryRequest = {
      year,
      productInventories,
    };

    return this.http.post(`${this.baseUrl}/customerproductinventory`, request);
  }

  importStockFromTelersdashboard(year: number): Observable<ImportStockFromTelersdashboardResponse> {
    return this.http.post<ImportStockFromTelersdashboardResponse>(`${this.baseUrl}/customerproductinventory/import/${year}`, {});
  }

  getCustomerProductInventory(year: number): Observable<GetCustomerProductInventoryResponse> {
    return this.http.get<GetCustomerProductInventoryResponse>(`${this.baseUrl}/customerproductinventory/${year}`);
  }

  createOrder(year: number, salesOrderPeriodIds: number[], deliveryPreference?: string) {
    if (this.env.ENABLE_SEND_ORDER) {
      const request: CreateOrderRequest = { year, salesOrderPeriodIds, deliveryPreference };
      return this.http.post<CreateOrderResponse>(`${this.baseUrl}/orderplan/create`, request);
    }

    return of({ id: -1 } as CreateOrderResponse);
  }

  upsertPreferedDeliveryPeriods(year: number, preferedDeliveryPeriods: number[]) {
    const request: UpsertPreferedDeliveryPeriodsRequest = {
      year,
      preferedDeliveryPeriods: preferedDeliveryPeriods,
    };
    return this.http.post(`${this.baseUrl}/customerpreference/prefereddeliveryperiods`, request);
  }

  upsertPeriodSystem(year: number, periodSystem: string) {
    const request: UpsertNumberOfPeriodsRequest = {
      year,
      periodSystem,
    };
    return this.http.post(`${this.baseUrl}/customerpreference/periodsystem`, request);
  }

  getCustomerPreferences(year: number) {
    return this.http.get<GetCustomerPreferencesResponse>(`${this.baseUrl}/customerpreference/${year}`);
  }

  getCropFields(cropYear: number) {
    return this.http.get<CropFieldsResponse>(`${this.baseUrl}/cropfield/${cropYear}`);
  }

  getShipTosWithOrders(cropYear: number, linkType: CropFieldLinkTypesEnum) {
    return this.http.get<number[]>(`${this.baseUrl}/orderline/ShiptosWithOrders/${linkType}/${cropYear}`);
  }

  saveLocationCropFields(cropYear: number, cropFieldIds: number[], cropFieldLinkType: CropFieldLinkTypesEnum) {
    return this.http.post(`${this.baseUrl}/cropfield/shipto`, { cropYear, cropFieldIds, cropFieldLinkType });
  }

  getLoggedInEmployee() {
    return this.http.get<Employee>(`${this.presalesOrderUrl}/advice/loggedinemployee`);
  }

  getOrderPlanOverviewList(year: number): Observable<GetOrderPlanResponse> {
    return this.http.get<GetOrderPlanResponse>(`${this.baseUrl}/orderplan/${year}`);
  }

  getDigitalProductsSubscriptions(year: number): Observable<GetProductSubscriptionResponse> {
    return this.http.get<GetProductSubscriptionResponse>(`${this.baseUrl}/product-subscription/${year}`);
  }

  saveDigitalProductsSubscriptions(digitalProducts: DigitalProductsToSave[], year: number): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/product-subscription/${year}`, digitalProducts);
  }

  getBemDigitalProductsSubscriptions(year: number): Observable<BemProduct[]> {
    return this.http.get<BemProduct[]>(`${this.baseUrl}/bem/product-order-line/${year}`);
  }

  getModuleActivation(module: 'blight' | 'downy-mildew' | 'virus-monitor'): Observable<boolean> {
    return this.http
      .get<ModuleActivationResponse>(`${this.arableDiseaseIntegrationUrl}/customer/is-activated`, { params: { module } })
      .pipe(map((response) => response.isActivated));
  }
}
